import axios from "axios";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { comPulseira } from "@/features/cliente-sessao";

import { StarRow, resolvePrimary } from "./estrelas";

const API = "/api";
const ESPERA_MS = 2500; // deixa o cardápio aparecer antes do pop-up
const OBRIGADO_MS = 4000; // o "Obrigado" fecha sozinho (dono, 24/10/2026)

const foto = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return url.startsWith("/uploads/") ? `/api${url}` : url;
};

// Uma vez por visita: fechou e abriu o site de novo, pergunta de novo. Só marca
// depois que o servidor respondeu — falha de rede não gasta a visita.
const chaveVisita = (clienteId) => `avaliacao-popup-visita-${clienteId}`;
const jaPerguntou = (clienteId) => { try { return sessionStorage.getItem(chaveVisita(clienteId)) === "1"; } catch { return false; } };
const marcarPerguntou = (clienteId) => { try { sessionStorage.setItem(chaveVisita(clienteId), "1"); } catch { /* aba anônima */ } };

/**
 * Pop-up "Avalie seu último pedido" no cardápio (dono, 24/10/2026): o cliente
 * que recebeu o link da avaliação no WhatsApp e não respondeu vê a mesma
 * avaliação (estrelas por pergunta + comentário opcional) quando volta ao
 * cardápio. Só com a campanha Avaliação de Pedidos ligada — quem decide é o
 * servidor (`GET /public/review/pendente/{cliente_id}`).
 *
 * Fechar no X ou fora = "agora não" (volta na próxima visita). "Não quero
 * avaliar" dispensa este pedido de vez.
 */
export default function PopupAvaliacao({ cliente, ativo = true }) {
  const clienteId = cliente?.id;
  const [dados, setDados] = useState(null); // resposta de /public/review/{token}
  const [token, setToken] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [notas, setNotas] = useState({});
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!ativo || !clienteId || jaPerguntou(clienteId)) return undefined;
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        // Com a pulseira do cliente (01/11/2026): a resposta traz o token da avaliação
        const r = await axios.get(`${API}/public/review/pendente/${clienteId}`, comPulseira());
        marcarPerguntou(clienteId);
        const tk = r.data?.pendente?.token;
        if (!tk || !vivo) return;
        const d = await axios.get(`${API}/public/review/${tk}`);
        if (!vivo || d.data?.review?.submitted_at || !(d.data?.dimensions || []).length) return;
        setToken(tk);
        setDados(d.data);
        setAberto(true);
      } catch { /* sem pop-up: o cardápio segue normal */ }
    }, ESPERA_MS);
    return () => { vivo = false; clearTimeout(t); };
  }, [ativo, clienteId]);

  useEffect(() => {
    if (!enviado) return undefined;
    const t = setTimeout(() => setAberto(false), OBRIGADO_MS);
    return () => clearTimeout(t);
  }, [enviado]);

  if (!dados) return null;

  const { color: primaria } = resolvePrimary(dados.tenant?.primary_color);
  const perguntas = dados.dimensions || [];
  const respondidas = perguntas.filter((p) => Number(notas[p.key]) > 0).length;
  const completo = respondidas === perguntas.length;
  const pedido = dados.pedido || {};
  const itens = (pedido.items || []).filter((i) => i?.nome);
  const fotos = itens.map((i) => foto(i.photo_url)).filter(Boolean).slice(0, 4);
  const nome = (cliente?.nome || pedido.cliente_nome || "").trim().split(/\s+/)[0] || "";
  const nomeCap = nome ? nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase() : "";
  const quando = pedido.created_at
    ? new Date(pedido.created_at).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit" })
    : "";

  const enviar = async () => {
    if (!completo) return;
    setEnviando(true);
    setErro("");
    try {
      await axios.post(`${API}/public/review/${token}`, { ratings: notas, comment: comentario.trim() || null });
      setEnviado(true);
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não deu para enviar. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  };

  const naoQuero = () => {
    axios.post(`${API}/public/review/${token}/dispensar`).catch(() => {});
    setAberto(false);
  };

  return (
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogContent hideClose aria-describedby={undefined} data-e2e="popup-avaliacao"
        className="p-0 gap-0 overflow-hidden border-0 w-[94vw] sm:max-w-md max-h-[92vh] rounded-2xl flex flex-col bg-zinc-50 dark:bg-zinc-950">
        <header className="relative text-white px-5 pt-6 pb-5 text-center shrink-0"
          style={{ backgroundImage: `linear-gradient(135deg, ${primaria} 0%, color-mix(in srgb, ${primaria} 70%, #000) 100%)` }}>
          <button type="button" onClick={() => setAberto(false)} aria-label="Agora não"
            className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-white/15 hover:bg-white/25">
            <X className="w-4 h-4" />
          </button>
          {dados.tenant?.logo_url && (
            <img src={foto(dados.tenant.logo_url)} alt="" className="w-14 h-14 mx-auto rounded-2xl bg-white/15 p-1.5 object-contain shadow-lg mb-2"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <DialogTitle className="text-xl font-extrabold tracking-tight text-white px-8">
            {enviado ? (nomeCap ? `Obrigado, ${nomeCap}! 💛` : "Obrigado pela avaliação! 💛") : (nomeCap ? `${nomeCap}, como foi seu último pedido?` : "Como foi seu último pedido?")}
          </DialogTitle>
          {!enviado && (
            <div className="mt-2 flex items-center justify-center gap-2 text-sm text-white/90">
              {fotos.length > 0 && (
                <span className="flex -space-x-2">
                  {fotos.map((f, k) => <img key={k} src={f} alt="" className="w-7 h-7 rounded-full object-cover ring-2 ring-white/70 bg-white" />)}
                </span>
              )}
              <span className="truncate">Pedido {pedido.codigo}{quando ? ` · ${quando}` : ""}</span>
            </div>
          )}
        </header>

        {enviado ? (
          <div className="p-6 text-center space-y-4">
            <p className="text-5xl">🤩</p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">Sua avaliação ajuda {dados.tenant?.name || "a loja"} a melhorar cada pedido.</p>
            <button type="button" onClick={() => setAberto(false)} className="w-full h-12 rounded-xl text-white font-bold" style={{ backgroundColor: primaria }}>
              Voltar ao cardápio
            </button>
            {/* Barrinha do tempo até fechar sozinho */}
            <div className="h-1 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden" aria-hidden="true">
              <div className="h-full rounded-full" style={{ backgroundColor: primaria, animation: `avaliacao-obrigado ${OBRIGADO_MS}ms linear forwards` }} />
            </div>
            <style>{"@keyframes avaliacao-obrigado { from { width: 100%; } to { width: 0%; } }"}</style>
          </div>
        ) : (
          <div className="overflow-y-auto p-4 sm:p-5 space-y-4">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-4 space-y-6">
              {perguntas.map((p, idx) => {
                const feita = Number(notas[p.key]) > 0;
                return (
                  <div key={p.key}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className={`text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center ${feita ? "text-white" : "bg-zinc-200 dark:bg-zinc-800 text-zinc-500"}`}
                        style={feita ? { backgroundColor: primaria } : undefined}>
                        {feita ? "✓" : idx + 1}
                      </span>
                      <p className="text-base font-bold text-center text-zinc-900 dark:text-white">{p.label}</p>
                    </div>
                    <StarRow value={notas[p.key] || 0} onChange={(v) => setNotas((n) => ({ ...n, [p.key]: v }))} primary={primaria} tamanho="w-10 h-10 sm:w-11 sm:h-11" />
                  </div>
                );
              })}
              <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                  Quer contar algo a mais? <span className="text-zinc-400 normal-case font-normal">(opcional)</span>
                </label>
                <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} maxLength={500}
                  placeholder="Conta pra gente o que você achou…"
                  className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950/60 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ "--tw-ring-color": primaria }} />
              </div>
            </div>
            {erro && <p className="text-sm text-red-500 text-center">{erro}</p>}
            <button type="button" onClick={enviar} disabled={enviando || !completo} data-e2e="popup-avaliacao-enviar"
              className="w-full py-3.5 rounded-xl text-white font-bold text-base shadow-lg transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                backgroundImage: completo ? `linear-gradient(135deg, ${primaria}, color-mix(in srgb, ${primaria} 70%, #000))` : undefined,
                backgroundColor: completo ? undefined : "#71717a",
              }}>
              {enviando
                ? <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Enviando…</span>
                : completo ? "Enviar avaliação" : `Faltam ${perguntas.length - respondidas} nota${perguntas.length - respondidas > 1 ? "s" : ""}`}
            </button>
            <button type="button" onClick={naoQuero} data-e2e="popup-avaliacao-dispensar"
              className="w-full text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 py-1">
              Não quero avaliar
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
